import React, { useRef } from 'react';

import PropTypes from 'prop-types';
import { ProductCardCollection as PalProductCardCollection } from 'web-palette/dist/components/ProductCardCollection/ProductCardCollection';
import { actionFormatter } from 'web-palette/dist/util/actionFormatter/actionFormatter';

import { useIntersectionProcessor } from '@ulta/core/hooks/useIntersectionProcessor/useIntersectionProcessor';
import { getButtonHtml } from '@ulta/core/utils/getButtonHtml/getButtonHtml';
import { getLinkHtml } from '@ulta/core/utils/getLinkHtml/getLinkHtml';

/**
  * Represents an ProductCardCollection component
  * @method
  * @param {ProductCardCollectionProps} props - React properties passed from composition
  * @returns PalProductCardCollection
  */
const ProductCardCollection = ( props ) => {
  const {
    /* actions */
    action,
    fullyClickableParent,

    /* Array of images */
    imagesArray,

    /* text */
    subtitle,
    subtitleTag,
    title,
    titleTag,

    /* color, theme and elevation */
    backgroundColor,
    elevation,
    theme
  } = props;

  /** variables */
  const productCollectionRef = useRef( null );

  /** Objects for external media and actions coming from Amplience */
  let formattedActionFullyClickableable;

  useIntersectionProcessor( {
    ...props,
    componentRef: productCollectionRef
  } );

  if( action && fullyClickableParent ){
    formattedActionFullyClickableable = actionFormatter( {
      action,
      fullyClickableParent,
      textForScreenReaders: title
    }, { getButtonMarkup: getButtonHtml, getLinkMarkup: getLinkHtml } );
  }

  return (
    <div
      className='ProductCardCollection'
      ref={ productCollectionRef }
    >
      <PalProductCardCollection
        backgroundColor={ backgroundColor }
        elevation={ elevation }
        externalActionElement={ formattedActionFullyClickableable }
        externalImageElements={ imagesArray }
        fullyClickableParent={ fullyClickableParent }
        subtitle={ subtitle }
        subtitleTag={ subtitleTag }
        theme={ theme }
        title={ title }
        titleTag={ titleTag }
      />
    </div>
  );
};

/**
 * Property type definitions
 * @typedef ProductCardCollectionProps
 * @type {object}
 * @property {object} action - click action for the card.
 * @property {string} backgroundColor - string value that sets the background color of the card.
 * @property {string} elevation - string value that sets the elevation.
 * @property {boolean} fullyClickableParent - boolean value to set if the parent is fully clickable.
 * @property {Array} imagesArray - array of image elements.
 * @property {string} subtitle - string value that sets the subtitle text.
 * @property {string} subtitleTag - string value that tag of subtitle(e.g: p, span,etc.)
 * @property {string} theme - sets the theme at the component level.
 * @property {string} title - string value that sets the title text.
 * @property {string} titleTag - string value that sets the tag of title.
 */
export const propTypes = {
  /**
  * Accepts click action for the card.
  */
  action: PropTypes.object,
  /**
  * Sets the background color for ProductCardCollection
  */
  backgroundColor: PropTypes.string,
  /**
   * String to set the elevation of the card.
   */
  elevation: PropTypes.string,
  /**
   * Boolean value to show that the card is fully clickable
  */
  fullyClickableParent: PropTypes.bool,
  /**
  * array of image elements.
  */
  imagesArray: PropTypes.array,
  /**
   * Sets the subtitle text of the Card
   */
  subtitle: PropTypes.string,
  /**
   * Sets the html tag for subtitle
   */
  subtitleTag: PropTypes.string,
  /**
   * Sets the theme at component level
  */
  theme: PropTypes.string,
  /**
   * Sets the title text of the ProductCardCollection
  */
  title: PropTypes.string,
  /**
   * Sets the html tag for title
  */
  titleTag: PropTypes.string
};
export default ProductCardCollection;